/**
 * Shared utilities for action buttons across row and toolbar contexts
 *
 * Eliminates code duplication between:
 * - AlwaysVisibleActions.tsx
 * - BulkLifecycleToolbarContent.tsx
 * - ActionsDropdown.tsx
 */

import type { ReactNode } from 'react'
import { Modal } from 'antd'
import * as Icons from '@ant-design/icons'
import type { BulkAction } from './BulkLifecycleToolbarContent'

/**
 * Ant Design Modal animation duration in milliseconds.
 * This matches the CSS transition duration used by Ant Design's Modal component.
 * We add a small buffer (50ms) to ensure the animation is fully complete.
 */
const MODAL_ANIMATION_DURATION = 250

/**
 * Render an Ant Design icon by name
 */
export function renderIcon(iconName?: string): ReactNode {
  if (!iconName) return null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const IconComponent = (Icons as any)[iconName]
  return IconComponent ? <IconComponent /> : null
}

/**
 * Generate confirmation message showing applicable/skipped counts
 *
 * @example Lifecycle action
 * "Activate 3 offerings? (2 not in draft state will be skipped)"
 * "Expire 5 offerings?"
 *
 * @example Record action
 * "Delete 3 offerings?"
 * "Restore 2 offerings? (1 not applicable will be skipped)"
 */
export function generateConfirmMessage(
  action: BulkAction,
  entityLabel: string
): string {
  const base = `${action.label} ${action.applicableCount} ${entityLabel}?`

  if (action.skippedCount > 0) {
    // For lifecycle actions with fromStates, describe the valid states
    if (action.fromStates && action.fromStates.length > 0) {
      const validStates = action.fromStates.join(' or ')
      return `${base} (${action.skippedCount} not in ${validStates} state will be skipped)`
    }

    // For record actions or actions without fromStates, use a generic message
    return `${base} (${action.skippedCount} not applicable will be skipped)`
  }

  return base
}

/**
 * Generate tooltip for disabled actions
 */
export function getDisabledTooltip(
  action: BulkAction,
  selectedCount: number,
  entityLabel: string
): string {
  if (selectedCount === 0) {
    return `Select ${entityLabel} to ${action.label.toLowerCase()}`
  }
  if (action.applicableCount === 0) {
    // Explain why no items are applicable
    if (action.fromStates && action.fromStates.length > 0) {
      const validStates = action.fromStates.join(' or ')
      return `No selected ${entityLabel} are in ${validStates} state`
    }
    return `No selected ${entityLabel} can be ${action.label.toLowerCase()}d`
  }
  return ''
}

/**
 * Wait for a specified number of milliseconds.
 */
function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Show a confirmation modal and execute the action AFTER the modal is VISUALLY gone.
 *
 * PROBLEM SOLVED:
 * Modal.confirm's `afterClose` callback + requestAnimationFrame was not reliable
 * because `afterClose` may fire before the CSS fade-out animation completes visually.
 * This caused the progress modal to appear while the confirmation modal was still
 * visible, creating a jarring visual overlap.
 *
 * SOLUTION:
 * Use a state-based Modal component approach where we:
 * 1. Show confirmation modal
 * 2. On user confirmation, close modal and wait for animation duration
 * 3. THEN execute the action (showing progress modal)
 *
 * The explicit setTimeout delay matches Ant Design's modal animation duration
 * (approximately 200ms) plus a small buffer, ensuring the modal is fully
 * invisible before the progress modal appears.
 *
 * @param action - The bulk action to confirm and execute
 * @param entityLabel - Label for the entity type (e.g., "offerings")
 * @param onExecute - Async function to execute after confirmation
 */
export function showConfirmThenExecute(
  action: BulkAction,
  entityLabel: string,
  onExecute: () => Promise<void>
): void {
  // Create modal instance to have control over closing
  const modal = Modal.confirm({
    title: action.label,
    content: generateConfirmMessage(action, entityLabel),
    okText: 'Yes',
    cancelText: 'No',
    okButtonProps: action.danger ? { danger: true } : undefined,
    // Return a promise from onOk that waits for animation to complete
    // This keeps the modal's internal state updated while we control timing
    onOk: async () => {
      // Close the modal programmatically (this starts the close animation)
      modal.destroy()

      // Wait for the animation to complete visually
      // This is the key fix - we wait AFTER modal.destroy() is called
      await delay(MODAL_ANIMATION_DURATION)

      // Now execute the action - the confirmation modal is guaranteed to be gone
      await onExecute()
    },
  })
}
